import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Check, Phone, Mail, Star,
  Zap, Eye, Network, Lock, Wifi, Bell, Server, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { getServices } from '../lib/api';

const iconMap = {
  Zap, Eye, Network, Lock, Wifi, Bell, Server, Settings
};

// Service-specific SEO content
const serviceContent = {
  "fiber-optic": {
    name: "Fiber Optic Installation",
    name_es: "Instalación de Fibra Óptica",
    icon: "Zap",
    heroImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200",
    description: "Professional fiber optic cable installation for businesses across Florida. We provide high-speed, reliable connectivity solutions for commercial and industrial applications.",
    description_es: "Instalación profesional de cable de fibra óptica para empresas en toda Florida. Proporcionamos soluciones de conectividad confiables y de alta velocidad para aplicaciones comerciales e industriales.",
    benefits: [
      { en: "Speeds up to 100 Gbps", es: "Velocidades hasta 100 Gbps" },
      { en: "Immune to electromagnetic interference", es: "Inmune a interferencias electromagnéticas" },
      { en: "Future-proof infrastructure", es: "Infraestructura preparada para el futuro" },
      { en: "Lower latency than copper", es: "Menor latencia que el cobre" },
      { en: "Secure data transmission", es: "Transmisión de datos segura" },
      { en: "Long-distance capability", es: "Capacidad de larga distancia" }
    ],
    applications: [
      { en: "Data Centers", es: "Centros de Datos" },
      { en: "Office Buildings", es: "Edificios de Oficinas" },
      { en: "Healthcare Facilities", es: "Instalaciones de Salud" },
      { en: "Educational Institutions", es: "Instituciones Educativas" },
      { en: "Government Buildings", es: "Edificios Gubernamentales" }
    ],
    keywords: ["fiber optic installation Florida", "commercial fiber optic", "fiber cable contractor", "enterprise fiber solutions"],
    priceRange: "$2,500 - $50,000+"
  },
  "cctv-systems": {
    name: "CCTV & Security Cameras",
    name_es: "CCTV y Cámaras de Seguridad",
    icon: "Eye",
    heroImage: "https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=1200",
    description: "State-of-the-art CCTV and security camera systems for complete property protection. We install IP cameras, analog systems, and advanced video analytics solutions.",
    description_es: "Sistemas de CCTV y cámaras de seguridad de última generación para protección completa de propiedades. Instalamos cámaras IP, sistemas analógicos y soluciones avanzadas de análisis de video.",
    benefits: [
      { en: "4K Ultra HD resolution", es: "Resolución 4K Ultra HD" },
      { en: "Remote viewing via smartphone", es: "Visualización remota via smartphone" },
      { en: "Night vision capability", es: "Capacidad de visión nocturna" },
      { en: "Motion detection alerts", es: "Alertas de detección de movimiento" },
      { en: "Cloud and local storage", es: "Almacenamiento en nube y local" },
      { en: "AI-powered analytics", es: "Análisis con inteligencia artificial" }
    ],
    applications: [
      { en: "Retail Stores", es: "Tiendas Minoristas" },
      { en: "Warehouses", es: "Almacenes" },
      { en: "Parking Lots", es: "Estacionamientos" },
      { en: "Office Buildings", es: "Edificios de Oficinas" },
      { en: "Residential Properties", es: "Propiedades Residenciales" }
    ],
    keywords: ["CCTV installation Florida", "security cameras", "video surveillance", "IP camera systems"],
    priceRange: "$1,500 - $25,000+"
  },
  "structured-cabling": {
    name: "Structured Cabling",
    name_es: "Cableado Estructurado",
    icon: "Network",
    heroImage: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1200",
    description: "Professional structured cabling solutions for reliable network infrastructure. We design and install Cat5e, Cat6, Cat6a, and Cat8 cabling systems.",
    description_es: "Soluciones profesionales de cableado estructurado para infraestructura de red confiable. Diseñamos e instalamos sistemas de cableado Cat5e, Cat6, Cat6a y Cat8.",
    benefits: [
      { en: "Organized cable management", es: "Gestión de cables organizada" },
      { en: "Easy troubleshooting", es: "Solución de problemas fácil" },
      { en: "Scalable infrastructure", es: "Infraestructura escalable" },
      { en: "Industry standard compliance", es: "Cumplimiento de estándares" },
      { en: "Reduced downtime", es: "Tiempo de inactividad reducido" },
      { en: "Professional documentation", es: "Documentación profesional" }
    ],
    applications: [
      { en: "New Construction", es: "Nueva Construcción" },
      { en: "Office Renovations", es: "Renovaciones de Oficinas" },
      { en: "Data Centers", es: "Centros de Datos" },
      { en: "Industrial Facilities", es: "Instalaciones Industriales" },
      { en: "Multi-tenant Buildings", es: "Edificios Multi-inquilino" }
    ],
    keywords: ["structured cabling Florida", "network cabling", "Cat6 installation", "data cabling contractor"],
    priceRange: "$75 - $150 per drop"
  },
  "access-control": {
    name: "Access Control Systems",
    name_es: "Sistemas de Control de Acceso",
    icon: "Lock",
    heroImage: "https://images.unsplash.com/photo-1558002038-1055907df827?w=1200",
    description: "Advanced access control solutions to secure your facility. We install keycard systems, biometric readers, and integrated security platforms.",
    description_es: "Soluciones avanzadas de control de acceso para asegurar su instalación. Instalamos sistemas de tarjetas, lectores biométricos y plataformas de seguridad integradas.",
    benefits: [
      { en: "Keycard and fob systems", es: "Sistemas de tarjeta y llavero" },
      { en: "Biometric authentication", es: "Autenticación biométrica" },
      { en: "Mobile credentials", es: "Credenciales móviles" },
      { en: "Audit trail logging", es: "Registro de auditoría" },
      { en: "Integration with CCTV", es: "Integración con CCTV" },
      { en: "Remote management", es: "Gestión remota" }
    ],
    applications: [
      { en: "Corporate Offices", es: "Oficinas Corporativas" },
      { en: "Healthcare Facilities", es: "Instalaciones de Salud" },
      { en: "Government Buildings", es: "Edificios Gubernamentales" },
      { en: "Educational Campus", es: "Campus Educativos" },
      { en: "Manufacturing Plants", es: "Plantas de Manufactura" }
    ],
    keywords: ["access control Florida", "keycard systems", "biometric access", "security door systems"],
    priceRange: "$800 - $5,000 per door"
  },
  "networking": {
    name: "Network Infrastructure",
    name_es: "Infraestructura de Red",
    icon: "Wifi",
    heroImage: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1200",
    description: "Complete network infrastructure solutions including wireless access points, switches, routers, and enterprise networking equipment.",
    description_es: "Soluciones completas de infraestructura de red incluyendo puntos de acceso inalámbrico, switches, routers y equipos de red empresariales.",
    benefits: [
      { en: "Enterprise WiFi solutions", es: "Soluciones WiFi empresariales" },
      { en: "Network segmentation", es: "Segmentación de red" },
      { en: "High availability design", es: "Diseño de alta disponibilidad" },
      { en: "Managed switches", es: "Switches administrados" },
      { en: "VPN configuration", es: "Configuración VPN" },
      { en: "Network monitoring", es: "Monitoreo de red" }
    ],
    applications: [
      { en: "Enterprise Networks", es: "Redes Empresariales" },
      { en: "Hospitality WiFi", es: "WiFi para Hospitalidad" },
      { en: "Retail Networks", es: "Redes Retail" },
      { en: "Campus Networks", es: "Redes de Campus" },
      { en: "Industrial IoT", es: "IoT Industrial" }
    ],
    keywords: ["network installation Florida", "enterprise networking", "WiFi installation", "network infrastructure"],
    priceRange: "$1,200 - $50,000+"
  },
  "alarm-systems": {
    name: "Alarm & Intrusion Systems",
    name_es: "Sistemas de Alarma e Intrusión",
    icon: "Bell",
    heroImage: "https://images.unsplash.com/photo-1558002038-1055907df827?w=1200",
    description: "Comprehensive alarm and intrusion detection systems for commercial and residential properties. We install and monitor advanced security systems.",
    description_es: "Sistemas completos de alarma y detección de intrusión para propiedades comerciales y residenciales. Instalamos y monitoreamos sistemas de seguridad avanzados.",
    benefits: [
      { en: "24/7 monitoring available", es: "Monitoreo 24/7 disponible" },
      { en: "Motion sensors", es: "Sensores de movimiento" },
      { en: "Glass break detection", es: "Detección de rotura de vidrio" },
      { en: "Door/window contacts", es: "Contactos de puerta/ventana" },
      { en: "Smartphone alerts", es: "Alertas en smartphone" },
      { en: "Integration ready", es: "Listo para integración" }
    ],
    applications: [
      { en: "Retail Stores", es: "Tiendas Minoristas" },
      { en: "Warehouses", es: "Almacenes" },
      { en: "Office Buildings", es: "Edificios de Oficinas" },
      { en: "Residential Homes", es: "Hogares Residenciales" },
      { en: "Industrial Facilities", es: "Instalaciones Industriales" }
    ],
    keywords: ["alarm system Florida", "intrusion detection", "security alarm installation", "commercial alarm systems"],
    priceRange: "$1,000 - $15,000+"
  }
};

export default function ServicePage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const currentLanguage = language || 'en';
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const service = serviceContent[slug] || serviceContent["fiber-optic"];
  const IconComponent = iconMap[service.icon] || Zap;
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await getServices();
        setAllServices(servicesRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const pageTitle = currentLanguage === 'es' 
    ? `${service.name_es} en Florida | IQES`
    : `${service.name} in Florida | IQES`;
  
  const pageDescription = currentLanguage === 'es'
    ? service.description_es
    : service.description;

  // Schema.org Service markup
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": currentLanguage === 'es' ? service.name_es : service.name,
    "description": pageDescription,
    "provider": {
      "@type": "LocalBusiness",
      "name": "IQES",
      "telephone": "+1-786-981-3760",
      "areaServed": "Florida"
    },
    "areaServed": {
      "@type": "State",
      "name": "Florida"
    },
    "serviceType": service.name
  };

  const canonicalUrl = `https://iqeslowvoltage.com/services/${slug}`;

  // Update document title and meta tags via effect for compatibility  
  useEffect(() => {
    document.title = pageTitle;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', pageDescription);
    }
    
    // Update OG tags
    const updateMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    updateMeta('og:title', pageTitle);
    updateMeta('og:description', pageDescription);
    updateMeta('og:type', 'website');
    updateMeta('og:image', service.heroImage);
    
    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
    
    // Add JSON-LD schema
    let schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schemaData);
    
  }, [pageTitle, pageDescription, service.heroImage, canonicalUrl, schemaData]);

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero Section - WHITE background */}
        <section className="pt-28 pb-16 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 166, 81, 0.1)' }}>
                  <IconComponent className="w-7 h-7" style={{ color: '#00A651' }} />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: '#003366' }}>
                  {currentLanguage === 'es' ? service.name_es : service.name}
                </h1>
              </div>
              
              <p className="text-gray-600 text-lg mb-8">
                {currentLanguage === 'es' ? service.description_es : service.description}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/quote">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6">
                    {currentLanguage === 'es' ? 'Cotización' : 'Get Quote'}
                  </Button>
                </Link>
                <a href="tel:+17869813760">
                  <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                    <Phone className="mr-2 w-4 h-4" />
                    786-981-3760
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits & Applications Section - WHITE background */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#003366' }}>
                  {currentLanguage === 'es' ? 'Beneficios' : 'Benefits'}
                </h2>
                <div className="space-y-3">
                  {service.benefits.map((benefit, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#00A651' }} />
                      <span className="text-gray-700">
                        {currentLanguage === 'es' ? benefit.es : benefit.en}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#003366' }}>
                  {currentLanguage === 'es' ? 'Aplicaciones' : 'Applications'}
                </h2>
                <div className="space-y-3">
                  {service.applications.map((app, idx) => (
                    <Card key={idx} className="bg-white border border-gray-200">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 166, 81, 0.1)' }}>
                          <IconComponent className="w-5 h-5" style={{ color: '#00A651' }} />
                        </div>
                        <span className="text-gray-700 font-medium">
                          {currentLanguage === 'es' ? app.es : app.en}
                        </span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Other Services - WHITE background, NO prices */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-2xl font-bold" style={{ color: '#003366' }}>
                {currentLanguage === 'es' ? 'Otros Servicios' : 'Other Services'}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {Object.entries(serviceContent)
                .filter(([key]) => key !== slug)
                .slice(0, 3)
                .map(([key, svc], idx) => {
                  const SvcIcon = iconMap[svc.icon] || Zap;
                  return (
                    <Link key={key} to={`/services/${key}`}>
                      <Card className="bg-white border border-gray-200 hover:border-[#00A651] hover:shadow-md transition-all h-full">
                        <CardContent className="p-5">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(0, 166, 81, 0.1)' }}>
                            <SvcIcon className="w-5 h-5" style={{ color: '#00A651' }} />
                          </div>
                          <h3 className="text-lg font-semibold mb-2" style={{ color: '#003366' }}>
                            {currentLanguage === 'es' ? svc.name_es : svc.name}
                          </h3>
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {currentLanguage === 'es' ? svc.description_es : svc.description}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>

        {/* CTA Section - Gradient */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 166, 81, 0.25) 100%)' }}>
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {currentLanguage === 'es' 
                  ? '¿Listo para comenzar?'
                  : 'Ready to Get Started?'}
              </h2>
              <p className="text-white/70 mb-8">
                {currentLanguage === 'es'
                  ? 'Contáctenos para una consulta gratuita y presupuesto personalizado.'
                  : 'Contact us for a free consultation and customized quote.'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/quote">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                    {currentLanguage === 'es' ? 'Solicitar Cotización' : 'Request Quote'}
                  </Button>
                </Link>
                <a href="tel:+17869813760">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                    <Phone className="mr-2 w-4 h-4" />
                    786-981-3760
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
